import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, Eye, Edit, Mail, Calendar, Clock, CheckCircle, XCircle, AlertCircle, Users, Lock, Trash } from 'lucide-react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import StaffPasswordModal from './StaffPasswordModal';


const StaffTable = ({ 
  staff = [], 
  loading = false, 
  selectedStaff = [], 
  onSelectionChange, 
  onStaffAction 
}) => {
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedRows, setExpandedRows] = useState([]);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [selectedStaffForPassword, setSelectedStaffForPassword] = useState(null);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort staff data
  const sortedStaff = [...staff]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];

    // Handle nested fields
    if (sortField === 'name') {
      aValue = a?.user_profiles?.full_name || '';
      bValue = b?.user_profiles?.full_name || '';
    } else if (sortField === 'email') {
      aValue = a?.user_profiles?.email || '';
      bValue = b?.user_profiles?.email || '';
    } else if (sortField === 'assignments') {
      aValue = a?.staff_event_assignments?.length || 0;
      bValue = b?.staff_event_assignments?.length || 0;
    }

    if (typeof aValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue?.localeCompare(bValue)
        : bValue?.localeCompare(aValue);
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Handle row selection
  const handleRowSelect = (staffId, checked) => {
    if (checked) {
      onSelectionChange([...selectedStaff, staffId]);
    } else {
      onSelectionChange(selectedStaff?.filter(id => id !== staffId));
    }
  };

  // Handle select all
  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(staff?.map(member => member?.id) || []);
    } else {
      onSelectionChange([]);
    }
  };

  // Toggle expanded row
  const toggleExpandedRow = (staffId) => {
    if (expandedRows?.includes(staffId)) {
      setExpandedRows(expandedRows?.filter(id => id !== staffId));
    } else {
      setExpandedRows([...expandedRows, staffId]);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      inactive: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
      suspended: { color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };

    const badge = badges?.[status] || badges?.inactive;
    const Icon = badge?.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge?.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  // Get assignment status
  const getActiveAssignments = (assignments) => {
    if (!assignments?.length) return 0;
    return assignments?.filter(assignment => 
      assignment?.status === 'assigned' && 
      new Date(assignment?.events?.event_date) > new Date()
    )?.length;
  };

  // Render sort header
  const SortHeader = ({ field, children, className = "" }) => (
    <th 
      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50 ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortField === field && (
          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
        )}
      </div>
    </th>
  );

  const handleSetPassword = (staffMember) => {
    setSelectedStaffForPassword(staffMember);
    setPasswordModalOpen(true);
  };

  const handlePasswordModalClose = () => {
    setPasswordModalOpen(false);
    setSelectedStaffForPassword(null);
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-16 bg-gray-100 mb-4"></div>
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="h-16 bg-gray-50 mb-2"></div>
        ))}
      </div>
    );
  }

  if (!staff?.length) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Staff Members</h3>
        <p className="text-gray-500 mb-4">Get started by adding your first staff member.</p>
        <button 
          onClick={() => onStaffAction?.('add')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Add Staff Member
        </button>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedStaff?.length === staff?.length && staff?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </th>
              <SortHeader field="name">Name</SortHeader>
              <SortHeader field="staff_role">Role</SortHeader>
              <SortHeader field="status">Status</SortHeader>
              <SortHeader field="assignments">Active Events</SortHeader>
              <SortHeader field="last_activity">Last Activity</SortHeader>
              <SortHeader field="created_at">Hire Date</SortHeader>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedStaff?.map((member, index) => {
              const isSelected = selectedStaff?.includes(member?.id);
              const isExpanded = expandedRows?.includes(member?.id);
              const activeAssignments = getActiveAssignments(member?.staff_event_assignments);

              return (
                <React.Fragment key={member?.id}>
                  <motion.tr 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleRowSelect(member?.id, e?.target?.checked)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                          <span className="text-blue-600 font-medium">
                            {member?.user_profiles?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {member?.user_profiles?.full_name || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <Mail className="w-3 h-3 mr-1" />
                            {member?.user_profiles?.email || 'No email'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {member?.staff_role?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'Staff'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(member?.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{activeAssignments}</span>
                        {member?.staff_event_assignments?.length > 0 && (
                          <button
                            onClick={() => toggleExpandedRow(member?.id)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member?.last_activity ? (
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {format(new Date(member?.last_activity), 'MMM d, yyyy')}
                        </div>
                      ) : (
                        'Never'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member?.hire_date ? format(new Date(member?.hire_date), 'MMM d, yyyy') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => onStaffAction?.('view', member)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      
                      <button
                        onClick={() => handleSetPassword(member)}
                        className="text-green-600 hover:text-green-800 transition-colors"
                        title="Set Password"
                      >
                        <Lock size={16} />
                      </button>

                      <button
                        onClick={() => onStaffAction?.('edit', member)}
                        className="text-indigo-600 hover:text-indigo-800 transition-colors"
                        title="Edit Staff"
                      >
                        <Edit size={16} />
                      </button>

                      <button
                        onClick={() => onStaffAction?.('assign', member)}
                        className="text-purple-600 hover:text-purple-800 transition-colors"
                        title="Assign to Event"
                      >
                        <Calendar size={16} />
                      </button>

                      <button
                        onClick={() => onStaffAction?.('delete', member)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Delete Staff"
                      >
                        <Trash size={16} />
                      </button>
                    </td>
                  </motion.tr>
                  {/* Expanded Row - Assignment Details */}
                  {isExpanded && member?.staff_event_assignments?.length > 0 && (
                    <tr className="bg-gray-50">
                      <td colSpan="8" className="px-6 py-4">
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-gray-900">Event Assignments</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {member?.staff_event_assignments?.map((assignment) => (
                              <div 
                                key={assignment?.id} 
                                className="bg-white border border-gray-200 rounded-lg p-3"
                              >
                                <h5 className="text-sm font-medium text-gray-900 mb-2">
                                  {assignment?.events?.title || 'Unknown Event'}
                                </h5>
                                <div className="space-y-1 text-xs text-gray-500">
                                  <div className="flex items-center">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    {assignment?.events?.event_date && 
                                      format(new Date(assignment?.events?.event_date), 'MMM d, yyyy HH:mm')
                                    }
                                  </div>
                                  {assignment?.role_description && (
                                    <div className="flex items-center">
                                      <Users className="w-3 h-3 mr-1" />
                                      {assignment?.role_description}
                                    </div>
                                  )}
                                  {assignment?.shift_start && assignment?.shift_end && (
                                    <div className="flex items-center">
                                      <Clock className="w-3 h-3 mr-1" />
                                      {format(new Date(assignment?.shift_start), 'HH:mm')} - 
                                      {format(new Date(assignment?.shift_end), 'HH:mm')}
                                    </div>
                                  )}
                                </div>
                                <div className="mt-2">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    assignment?.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
                                    assignment?.status === 'completed'? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {assignment?.status?.charAt(0)?.toUpperCase() + assignment?.status?.slice(1)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Table Footer */}
      <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{staff?.length || 0}</span> staff members
          </div>
          {selectedStaff?.length > 0 && (
            <div className="text-sm text-blue-600">
              {selectedStaff?.length} selected
            </div>
          )}
        </div>
      </div>
      {/* Password Modal */}
      <StaffPasswordModal
        isOpen={passwordModalOpen}
        onClose={handlePasswordModalClose}
        staffMember={selectedStaffForPassword}
      />
    </div>
  );
};

export default StaffTable;